<template>
  <div class="container-xl px-4 products-page">
    <div class="row">
      <div class="col-12">
        <div class="page-header-mobile d-flex justify-content-between align-items-center mb-3 mt-2">
          <h2 class="fw-bold mb-0 page-title-mobile">Gestión de Productos</h2>
          <!-- Entrada única de alta del catálogo (RF-1). El cuerpo del botón
               abre la carga manual en un clic —el caso frecuente— y la flecha
               despliega el camino asistido por IA. Se oculta entero sin
               `crear_productos`: los dos destinos exigen ese permiso, así que
               un botón a medias solo llevaría a un 403. -->
          <div
            v-if="puedeCrearProductos"
            ref="createMenuRoot"
            class="btn-group btn-mobile-primary new-product-split"
          >
            <button
              type="button"
              class="btn btn-dark d-flex align-items-center justify-content-center"
              @click="startManualCreate"
            >
              <i class="bi bi-plus-lg me-2"></i> Nuevo Producto
            </button>
            <button
              type="button"
              class="btn btn-dark dropdown-toggle dropdown-toggle-split new-product-split__toggle"
              :aria-expanded="showCreateMenu ? 'true' : 'false'"
              @click="toggleCreateMenu"
            >
              <span class="visually-hidden">Ver formas de cargar un producto</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end" :class="{ show: showCreateMenu }">
              <li>
                <button class="dropdown-item" type="button" @click="startManualCreate">
                  <i class="bi bi-pencil me-2"></i> Carga manual
                </button>
              </li>
              <li>
                <button class="dropdown-item" type="button" @click="goToAiImport">
                  <i class="bi bi-stars me-2"></i> Cargar con IA
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card p-3 mobile-filter-card">
          <div class="mobile-context-chip d-lg-none">
            <span class="mobile-context-label">Catálogo e Inventario</span>
            <strong>Productos</strong>
          </div>
          <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap mobile-filter-wrap">
            <div class="d-flex gap-2 flex-grow-1 mobile-filter-main">
              <input
                v-model="searchTerm"
                type="text"
                class="form-control mobile-search-input"
                placeholder="Buscar por nombre..."
              />

              <div class="position-relative mobile-category-picker">
                <input
                  v-model="categorySearchTerm"
                  type="text"
                  class="form-control"
                  placeholder="Filtrar por categoría..."
                  @focus="showCategoryDropdown = true"
                  @blur="handleCategoryBlur"
                  :disabled="loadingCategories"
                />

                <div
                  v-if="showCategoryDropdown && filteredCategories.length > 0"
                  class="category-dropdown position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                >
                  <div
                    class="dropdown-item cursor-pointer px-3 py-2 hover-bg-light"
                    @mousedown.prevent="selectCategory('', 'Todas las categorías')"
                  >
                    <strong>Todas las categorías</strong>
                  </div>
                  <div
                    v-for="cat in filteredCategories"
                    :key="cat.id"
                    class="dropdown-item cursor-pointer px-3 py-2 hover-bg-light"
                    @mousedown.prevent="selectCategory(cat.id, cat.name)"
                  >
                    {{ cat.name }}
                  </div>
                </div>
              </div>
            </div>
            <div class="form-check form-switch align-self-center mobile-noimage-switch">
              <input
                id="soloSinImagen"
                v-model="onlyWithoutImage"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label text-nowrap" for="soloSinImagen">Sin imagen</label>
            </div>
            <!-- RN-93: esta pantalla ve TODO por defecto. Es desde acá que se
                 administra la visibilidad, así que ocultar los inactivos sería
                 esconder lo único que se puede reactivar. -->
            <div class="align-self-center">
              <select v-model="filtroEstado" class="form-select" data-test="filtro-estado"
                      @change="loadProducts">
                <option value="todos">Todos</option>
                <option value="activos">Sólo activos</option>
                <option value="inactivos">Sólo inactivos</option>
              </select>
            </div>
            <button
              class="btn btn-outline-danger d-flex align-items-center mobile-clear-btn"
              @click="clearFilters"
              :disabled="!searchTerm && !selectedCategory && !onlyWithoutImage"
            >
              <i class="bi bi-arrow-counterclockwise me-2"></i>
              Limpiar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mobile-results-summary d-lg-none">
      <div class="mobile-results-pill">
        <span class="mobile-results-value">{{ filteredProducts.length }}</span>
        <span class="mobile-results-copy">productos</span>
      </div>
      <div v-if="selectedCategoryName" class="mobile-results-pill secondary">
        <span class="mobile-results-copy">Categoría</span>
        <span class="mobile-results-value small">{{ selectedCategoryName }}</span>
      </div>
    </div>

    <div class="card p-4 d-none d-lg-block">
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Imagen</th>
              <th>Código</th>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Stock Total</th>
              <th>Unidad</th>
              <th>IVA</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="9" class="text-center py-4">
                <i class="bi bi-arrow-clockwise spin me-2"></i>Cargando productos...
              </td>
            </tr>
            <tr v-else-if="pagedProducts.length === 0">
              <td colspan="9" class="text-center py-4 text-muted">
                No se encontraron productos
              </td>
            </tr>
            <tr v-else v-for="prod in pagedProducts" :key="prod.id">
              <td>
                <img
                  v-if="prod.tiene_imagen"
                  :src="prod.url_imagen"
                  class="product-thumb"
                  alt=""
                  loading="lazy"
                />
                <span v-else class="product-thumb product-thumb--empty" title="Sin imagen">
                  <i class="bi bi-image"></i>
                </span>
              </td>
              <td>{{ prod.code }}</td>
              <td>
                <div>
                  {{ prod.name }}
                  <!-- Que se VEA cuál está fuera de circulación: sin esto, la
                       pantalla que administra la visibilidad no la muestra. -->
                  <span v-if="prod.activo === false"
                        class="badge text-bg-secondary ms-1"
                        data-test="badge-inactivo">Desactivado</span>
                </div>
                <small class="text-muted" v-if="!prod.tiene_precio">
                  {{ prod.estado_producto }}
                </small>
              </td>
              <td>{{ prod.category }}</td>
              <td>{{ prod.stock }}</td>
              <td>{{ prod.unit }}</td>
              <td>{{ prod.iva }}</td>
              <td>
                <span
                  :class="[
                    'badge rounded-pill px-3 py-2',
                    prod.stock < prod.minStock
                      ? 'bg-light text-dark fw-bold border border-secondary'
                      : 'bg-dark text-white fw-bold'
                  ]"
                >
                  {{ prod.stock < prod.minStock ? 'Bajo Stock' : 'Normal' }}
                </span>
              </td>
              <td>
                <div class="d-flex gap-1">
                  <button
                    v-if="!prod.tiene_imagen"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="iaResolvingId === prod.id"
                    @click="resolveImageWithIA(prod)"
                    title="Buscar imagen con IA"
                  >
                    <i :class="iaResolvingId === prod.id ? 'bi bi-arrow-clockwise spin' : 'bi bi-stars'"></i>
                  </button>
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="openProductModal(prod)"
                    title="Editar producto"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    v-if="puedeEditarProductos && prod.activo !== false"
                    class="btn btn-outline-warning btn-sm"
                    data-test="btn-desactivar"
                    @click="desactivarProducto(prod.id)"
                    title="Desactivar: deja de ofrecerse al cobrar y al vender"
                  >
                    <i class="bi bi-eye-slash"></i>
                  </button>
                  <button
                    v-else-if="puedeEditarProductos"
                    class="btn btn-outline-success btn-sm"
                    data-test="btn-activar"
                    @click="activarProducto(prod.id)"
                    title="Reactivar"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                  <button
                    v-if="puedeEliminarProductos"
                    class="btn btn-outline-danger btn-sm"
                    data-test="btn-eliminar"
                    @click="deleteProduct(prod.id)"
                    title="Eliminar producto"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="mobile-product-list d-lg-none">
      <div v-if="loading" class="mobile-empty-state">
        <i class="bi bi-arrow-clockwise spin"></i>
        <span>Cargando productos...</span>
      </div>

      <div v-else-if="pagedProducts.length === 0" class="mobile-empty-state">
        <i class="bi bi-inboxes"></i>
        <span>No se encontraron productos</span>
      </div>

      <div
        v-else
        v-for="prod in pagedProducts"
        :key="`mobile-${prod.id}`"
        class="mobile-product-card"
      >
        <div class="mobile-product-top">
          <div class="d-flex align-items-start gap-2">
            <img
              v-if="prod.tiene_imagen"
              :src="prod.url_imagen"
              class="product-thumb"
              alt=""
              loading="lazy"
            />
            <span v-else class="product-thumb product-thumb--empty" title="Sin imagen">
              <i class="bi bi-image"></i>
            </span>
            <div>
              <p class="mobile-product-code">{{ prod.code || 'Sin código' }}</p>
              <h3 class="mobile-product-name">{{ prod.name }}</h3>
            </div>
          </div>
          <span
            class="mobile-stock-badge"
            :class="{ low: prod.stock < prod.minStock }"
          >
            {{ prod.stock < prod.minStock ? 'Bajo stock' : 'Normal' }}
          </span>
        </div>

        <div class="mobile-product-meta">
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Categoría</span>
            <strong>{{ prod.category || 'Sin categoría' }}</strong>
          </div>
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Stock</span>
            <strong>{{ prod.stock }}</strong>
          </div>
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Unidad</span>
            <strong>{{ prod.unit || 'Sin unidad' }}</strong>
          </div>
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">IVA</span>
            <strong>{{ prod.iva || 'Sin IVA' }}</strong>
          </div>
        </div>

        <p v-if="!prod.tiene_precio && prod.estado_producto" class="mobile-product-note">
          {{ prod.estado_producto }}
        </p>

        <div class="mobile-product-actions">
          <button
            v-if="!prod.tiene_imagen"
            class="btn btn-outline-primary btn-sm"
            :disabled="iaResolvingId === prod.id"
            @click="resolveImageWithIA(prod)"
          >
            {{ iaResolvingId === prod.id ? 'Buscando…' : 'Imagen con IA' }}
          </button>
          <button
            class="btn btn-outline-secondary btn-sm"
            @click="openProductModal(prod)"
          >
            Editar
          </button>
          <button
            v-if="puedeEditarProductos && prod.activo !== false"
            class="btn btn-outline-warning btn-sm"
            @click="desactivarProducto(prod.id)"
          >
            Desactivar
          </button>
          <button
            v-else-if="puedeEditarProductos"
            class="btn btn-outline-success btn-sm"
            @click="activarProducto(prod.id)"
          >
            Reactivar
          </button>
          <button
            v-if="puedeEliminarProductos"
            class="btn btn-outline-danger btn-sm"
            @click="deleteProduct(prod.id)"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>

    <RegistrarProductoModal
      v-model:showModal="showProductModal"
      :title="editingProduct ? 'Editar producto' : 'Registrar producto'"
      :initial-code="editingProduct?.code ?? ''"
      :product="editingProduct"
      @product-registered="onProductRegistered"
    />

    <AppPagination
      class="mt-3 d-none d-lg-flex"
      :total-pages="effectiveTotalPages"
      :current-page="currentPage"
      @page-changed="changePage"
    />
    <AppPagination
      class="mt-3 d-lg-none"
      :total-pages="effectiveTotalPages"
      :current-page="currentPage"
      @page-changed="changePage"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import RegistrarProductoModal from '@/components/RegistrarProductoModal.vue'
import AppPagination           from '@/components/AppPagination.vue'
import apiService from '@/services/apiService.js'
import { hasPermission } from '@/services/authSessionService'

const router = useRouter()

// `POST /api/products` y la ruta del agente exigen `crear_productos`
// (productsRoutes.js). No se ofrece una acción que el API va a rechazar.
const puedeCrearProductos = computed(() => hasPermission('crear_productos'))
// SPEC_BAJA_DE_PRODUCTOS 5 / PB-3. Desactivar y reactivar van con
// `editar_productos` —son reversibles y son mantenimiento de catálogo—; borrar
// se queda en `eliminar_productos`, que sólo tiene `administrador`.
//
// **Los botones no estaban gateados**: lo destapó el E2E de permisos, no la
// lectura. La ruta ya exigía el permiso, así que nadie sin él llegaba a verlos
// — pero ofrecer un botón que la API va a rechazar es el defecto A-11 otra vez.
const puedeEditarProductos = computed(() => hasPermission('editar_productos'))
const puedeEliminarProductos = computed(() => hasPermission('eliminar_productos'))

// Desplegable del botón partido de alta.
//
// El estado se maneja acá y no con `data-bs-toggle="dropdown"`: el data-api de
// dropdowns de Bootstrap no está activo en esta app —verificado en navegador,
// el toggle no cambia `aria-expanded`— y los desplegables del navbar abren por
// CSS (`.navbar-nav .dropdown:hover/:focus-within`), no por JS. Con la clase
// `show` alcanza para que la hoja de estilos de Bootstrap lo muestre; el
// cierre por clic fuera y por `Escape` se resuelve acá.
const showCreateMenu = ref(false)
const createMenuRoot = ref(null)

function toggleCreateMenu() {
  showCreateMenu.value = !showCreateMenu.value
}

function closeCreateMenu() {
  showCreateMenu.value = false
}

function handleCreateMenuOutside(event) {
  if (!showCreateMenu.value) return
  // El clic sobre el propio botón partido lo maneja `toggleCreateMenu`.
  if (createMenuRoot.value?.contains(event.target)) return
  closeCreateMenu()
}

function handleCreateMenuKeydown(event) {
  if (event.key === 'Escape') closeCreateMenu()
}

onMounted(() => {
  document.addEventListener('click', handleCreateMenuOutside)
  document.addEventListener('keydown', handleCreateMenuKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleCreateMenuOutside)
  document.removeEventListener('keydown', handleCreateMenuKeydown)
})

function startManualCreate() {
  closeCreateMenu()
  openProductModal()
}

function goToAiImport() {
  closeCreateMenu()
  router.push({ name: 'InvImportacionProductos' })
}

const searchTerm = ref('')
const categorySearchTerm = ref('')
const showCategoryDropdown = ref(false)
const products = ref([])
const categories = ref([])
const allCategories = ref([]) // Todas las categorías cargadas
const selectedCategory = ref('') // ID de la categoría seleccionada
const selectedCategoryName = ref('') // Nombre de la categoría seleccionada (para mostrar)
const loading = ref(false)
const loadingCategories = ref(false)
// Filtro "sin imagen" + acción por fila "Buscar imagen con IA" (BIC-7B-002)
const onlyWithoutImage = ref(false)
// RN-93: `todos` por defecto. El backend ya devuelve todo (su default es
// inclusivo, PB-2) y acá se elige qué mostrar — un solo lugar, que cubre las
// cuatro ramas de `loadProducts`.
const filtroEstado = ref('todos')
const iaResolvingId = ref(null)
const currentPage = ref(1)
const totalPages = ref(1)
const pagination = ref({})
const itemsPerPage = 10 // Para paginación local cuando usamos búsqueda o filtro de categoría

// Función helper para mapear tipo_iva a descripción
function getTipoIvaDescripcion(tipo_iva) {
  if (tipo_iva === 1) return 'IVA 10%'
  if (tipo_iva === 2) return 'IVA 5%'
  if (tipo_iva === 3) return 'Exenta'
  return ''
}

// Función para mapear productos del backend
function mapProduct(item) {
  const urlImagen = item.url_imagen || ''
  // La convención legacy (url_imagen = código de barras/producto) cuenta como
  // "sin imagen propia" — mismo criterio que la auditoría del backfill.
  const tieneImagen = !!urlImagen
    && urlImagen !== item.codigo_barras
    && urlImagen !== item.codigo_producto
  return {
    id: item.producto_id,
    code: item.codigo_barras || item.codigo_producto || '',
    name: item.nombre || '',
    category: item.categoria_nombre || '',
    stock: item.stock_disponible || 0,
    minStock: 5, // valor por defecto ya que no viene del backend
    unit: item.unidad_medida_nombre || '',
    iva: item.tipo_iva_descripcion || getTipoIvaDescripcion(item.tipo_iva),
    activo: item.activo,
    categoria_id: item.categoria_id,
    unidad_medida_id: item.unidad_medida_id,
    tipo_iva: item.tipo_iva,
    descripcion: item.descripcion || '',
    url_imagen: urlImagen,
    tiene_imagen: tieneImagen,
    precio_venta_actual: item.precio_venta_actual,
    tiene_precio: item.disponibilidad?.tiene_precio || false,
    estado_producto: item.estado_producto || ''
  }
}

// Cargar categorías desde el backend (todas las páginas)
async function loadCategories() {
  loadingCategories.value = true
  try {
    const allCategoriesData = []
    let currentPage = 1
    let hasMorePages = true
    
    // Cargar todas las páginas de categorías
    while (hasMorePages) {
      const response = await apiService.get(`/api/categories?page=${currentPage}&limit=50`)
      
      // Manejar diferentes formatos de respuesta
      let list = []
      let paginationInfo = null
      const data = response.data
      
      if (Array.isArray(data)) {
        list = data
        hasMorePages = false // No hay paginación
      } else if (data?.data) {
        if (Array.isArray(data.data)) {
          list = data.data
          hasMorePages = false
        } else if (Array.isArray(data.data.data)) {
          list = data.data.data
          paginationInfo = data.data.pagination
          hasMorePages = paginationInfo?.hasNextPage || false
        }
      }
      
      // Agregar categorías de esta página
      allCategoriesData.push(...list)
      
      // Si no hay más páginas, salir del loop
      if (!hasMorePages) break
      
      currentPage++
      
      // Protección contra loops infinitos
      if (currentPage > 10) {
        console.warn('[ProductosView] Se alcanzó el límite de 10 páginas al cargar categorías')
        break
      }
    }
    
    // Guardar todas las categorías
    allCategories.value = allCategoriesData.map(cat => ({
      id: cat.categoria_id ?? cat.id,
      name: cat.nombre
    }))
    
    // Inicializar categories con todas para el filtrado
    categories.value = [...allCategories.value]
    
  } catch (error) {
    console.error('Error cargando categorías:', error)
    allCategories.value = []
    categories.value = []
  } finally {
    loadingCategories.value = false
  }
}

// Filtrar categorías según el término de búsqueda
const filteredCategories = computed(() => {
  if (!categorySearchTerm.value.trim()) {
    return allCategories.value
  }
  
  const term = categorySearchTerm.value.toLowerCase()
  return allCategories.value.filter(cat => 
    cat.name.toLowerCase().includes(term)
  )
})

// Seleccionar una categoría del dropdown
function selectCategory(categoryId, categoryName = '') {
  selectedCategory.value = categoryId
  selectedCategoryName.value = categoryName
  categorySearchTerm.value = categoryName
  showCategoryDropdown.value = false
}


// Manejar blur del input de categoría
function handleCategoryBlur() {
  // Pequeño delay para permitir el click en el dropdown
  setTimeout(() => {
    showCategoryDropdown.value = false
  }, 200)
}

// Cargar productos desde el backend (con opción para silenciar alertas)
async function loadProducts(options = {}) {
  const { suppressAlert = false, page = 1 } = options || {}
  loading.value = true
  try {
    let response
    let data
    let usePaginacionLocal = false
    
    // Determinar qué endpoint usar según los filtros activos
    if (searchTerm.value.trim()) {
      // 1. Si hay término de búsqueda, usar el endpoint de búsqueda
      response = await apiService.get(`/api/products/search?query=${encodeURIComponent(searchTerm.value)}`)
      data = Array.isArray(response.data) ? response.data : []
      usePaginacionLocal = true
      
    } else if (onlyWithoutImage.value) {
      // Filtro "sin imagen": se trae un bloque grande de una vez y se filtra
      // y pagina localmente (el endpoint no conoce este filtro y el catálogo
      // es chico; evita mostrar páginas "vacías" con paginación del servidor).
      const categoryParam = selectedCategory.value ? `&category_id=${selectedCategory.value}` : ''
      response = await apiService.get(`/api/products?page=1&limit=500${categoryParam}`)
      data = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.data) ? response.data.data : [])
      usePaginacionLocal = true

    } else if (selectedCategory.value) {
      // 2. Si hay categoría seleccionada (sin búsqueda), usar endpoint de categoría con paginación
      response = await apiService.get(`/api/products?category_id=${selectedCategory.value}&page=${page}`)
      
      // Manejar la respuesta (similar al endpoint normal)
      if (Array.isArray(response.data)) {
        data = response.data
        usePaginacionLocal = true // Si viene como array simple, usar paginación local
      } else if (response.data?.data) {
        data = Array.isArray(response.data.data) ? response.data.data : []
        // Si tiene paginación del servidor, usarla
        if (response.data.pagination) {
          usePaginacionLocal = false
        } else {
          usePaginacionLocal = true
        }
      } else {
        data = []
        usePaginacionLocal = true
      }
      
    } else {
      // 3. Sin filtros, usar el endpoint normal con paginación del servidor
      response = await apiService.get(`/api/products?page=${page}`)
      
      // La respuesta tiene estructura { data: [...], pagination: {...} }
      data = Array.isArray(response.data) 
        ? response.data 
        : (Array.isArray(response.data?.data) ? response.data.data : [])
      
      usePaginacionLocal = false
      
    }
    
    // Mapear productos
    products.value = data.map(mapProduct)
    
    // Configurar paginación
    if (usePaginacionLocal) {
      // Paginación local para búsqueda o filtro de categoría
      const totalItems = products.value.length
      totalPages.value = Math.ceil(totalItems / itemsPerPage) || 1
      currentPage.value = Math.min(page, totalPages.value)
    } else {
      // Paginación del servidor
      if (response.data?.pagination) {
        pagination.value = response.data.pagination
        totalPages.value = response.data.pagination.totalPages || 1
        currentPage.value = response.data.pagination.page || 1
      } else {
        totalPages.value = 1
        currentPage.value = 1
      }
    }
    
  } catch (error) {
    console.error('Error cargando productos:', error)
    if (!suppressAlert) {
      alert('Error al cargar los productos. Revise la consola.')
    }
  } finally {
    loading.value = false
  }
}

// Las operaciones de crear/editar se realizan en el modal y aquí solo se refresca la lista.

// Eliminar producto — RN-96, RN-97.
//
// **El «Revise la consola» se va** (T-2). El backend ahora devuelve 409 con la
// causa nombrada y su conteo; la pantalla lo muestra y OFRECE la salida, que es
// la diferencia entre un error y un callejón sin salida. El operador reintentó
// a los 31 segundos porque nada le decía que reintentar era inútil.
async function deleteProduct (id) {
  const confirmed = await confirm({
    message: '¿Está seguro de que desea eliminar este producto?',
    title: 'Eliminar producto',
    confirmText: 'Eliminar',
    cancelText: 'Cancelar'
  })
  if (!confirmed) return

  try {
    await apiService.delete(`/api/products/${id}`)
    await loadProducts()
    alert.success('Producto eliminado correctamente.')
  } catch (error) {
    const data = error?.response?.data
    if (error?.response?.status === 409) {
      // El 409 trae el motivo. Si es historial, se ofrece desactivar en el
      // mismo diálogo: el operador no tiene que ir a buscar cómo seguir.
      if (data.situation === 'producto_con_historial') {
        const quiere = await confirm({
          title: 'No se puede eliminar',
          message: `${data.message}\n\n¿Querés desactivarlo?`,
          confirmText: 'Desactivar',
          cancelText: 'Cancelar'
        })
        if (quiere) await desactivarProducto(id)
        return
      }
      alert.error(data.message)
      return
    }
    console.error('Error eliminando producto:', error)
    alert.error(data?.message || 'No se pudo eliminar el producto.')
  }
}

// RN-91, RN-94, RN-95 — sacar de circulación sin borrar.
async function desactivarProducto (id) {
  try {
    await apiService.post(`/api/products/${id}/desactivar`, {})
    await loadProducts()
    alert.success('Producto desactivado: deja de ofrecerse al cobrar y al vender.')
  } catch (error) {
    alert.error(error?.response?.data?.message || 'No se pudo desactivar el producto.')
  }
}

async function activarProducto (id) {
  try {
    await apiService.post(`/api/products/${id}/activar`, {})
    await loadProducts()
    alert.success('Producto reactivado.')
  } catch (error) {
    alert.error(error?.response?.data?.message || 'No se pudo reactivar el producto.')
  }
}

const filteredProducts = computed(() => {
  let productsArray = Array.isArray(products.value) ? products.value : []

  // Si hay filtro de categoría Y búsqueda, aplicar filtro de categoría localmente
  // (porque el endpoint de búsqueda no acepta parámetro de categoría)
  if (searchTerm.value.trim() && selectedCategory.value) {
    productsArray = productsArray.filter(p => p.categoria_id == selectedCategory.value)
  }

  // Filtro "sin imagen" (siempre local — el endpoint no lo conoce)
  if (onlyWithoutImage.value) {
    productsArray = productsArray.filter(p => !p.tiene_imagen)
  }

  // Estado (RN-93). `activo !== false` y no `=== true`: una instancia que
  // todavía no migró puede traerlo `null`, y un producto sin la bandera se
  // trata como activo — mismo criterio que `COALESCE` en el backend.
  if (filtroEstado.value === 'activos') {
    productsArray = productsArray.filter(p => p.activo !== false)
  } else if (filtroEstado.value === 'inactivos') {
    productsArray = productsArray.filter(p => p.activo === false)
  }

  return productsArray
})

// Con búsqueda o filtro "sin imagen" la paginación es local sobre la lista
// filtrada; en el resto de los casos manda la paginación del servidor.
const isLocalPagination = computed(() => !!searchTerm.value.trim() || onlyWithoutImage.value)
const effectiveTotalPages = computed(() => (
  isLocalPagination.value
    ? (Math.ceil(filteredProducts.value.length / itemsPerPage) || 1)
    : totalPages.value
))

const showProductModal = ref(false)
const editingProduct  = ref(null)

// Mapea la respuesta de `GET /api/products/:id` al formato que espera
// `RegistrarProductoModal`. La fila de la lista (`mapProduct`) no alcanza para
// editar: no trae `codigo_barras`, `codigo_producto`, `codigo_plu_balanza` ni
// `es_granel`. Sin ellos el modal los hidrata vacíos y el PUT los borra al
// guardar — se perdía el código de barras y el PLU de todo producto a granel.
//
// `row` es la fila de la lista y se usa como respaldo campo por campo: si el
// GET falla, el modal abre con lo que ya se tenía en pantalla y nunca con
// menos de lo que mostraba antes de este cambio.
function mapProductForEdit(data, row) {
  return {
    id: data?.producto_id ?? data?.id ?? row.id,
    code: data?.codigo_barras || '',
    codigo_barras: data?.codigo_barras || '',
    codigo_producto: data?.codigo_producto || '',
    codigo_plu_balanza: data?.codigo_plu_balanza || '',
    es_granel: Boolean(data?.es_granel),
    name: data?.nombre || row.name || '',
    descripcion: data?.descripcion || row.descripcion || '',
    url_imagen: data?.url_imagen || row.url_imagen || '',
    categoria_id: data?.categoria_id ?? row.categoria_id ?? null,
    unidad_medida_id: data?.unidad_medida_id ?? row.unidad_medida_id ?? null,
    activo: data?.activo ?? row.activo ?? true,
    tipo_iva: data?.tipo_iva ?? row.tipo_iva ?? 1
  }
}

async function openProductModal(prod = null) {
  if (prod?.id) {
    // Edición: se pide el producto completo antes de abrir.
    // `GET /api/products/:id` devuelve el producto plano (verificado en
    // productsController.getProductById), no envuelto en `{ data }`.
    try {
      const { data } = await apiService.get(`/api/products/${prod.id}`)
      editingProduct.value = mapProductForEdit(data, prod)
    } catch (e) {
      // No se bloquea la edición: un producto que no se puede abrir porque
      // falló un GET auxiliar es peor que uno que se edita con datos parciales.
      console.error('Error cargando producto para edición:', e)
      editingProduct.value = mapProductForEdit(null, prod)
    }
  } else {
    // Alta
    editingProduct.value = null
  }
  showProductModal.value = true
}

async function onProductRegistered() {
  // El modal ya realizó el POST/PUT correctamente.
  // Sólo recargar lista en silencio y cerrar modal.
  try {
    await loadProducts({ suppressAlert: true, page: currentPage.value })
  } catch (e) {
    console.warn('Producto guardado, pero falló la recarga de productos:', e)
  }
  showProductModal.value = false
  editingProduct.value = null
}

function changePage(page) {
  if (page < 1 || page > effectiveTotalPages.value) return
  currentPage.value = page

  // Con paginación local (búsqueda o "sin imagen") no se recarga del servidor
  if (!isLocalPagination.value) {
    loadProducts({ suppressAlert: true, page })
  }
}

// Limpiar todos los filtros
function clearFilters() {
  searchTerm.value = ''
  selectedCategory.value = ''
  selectedCategoryName.value = ''
  categorySearchTerm.value = ''
  onlyWithoutImage.value = false
  currentPage.value = 1
  // Recargar productos sin filtros
  loadProducts({ suppressAlert: true })
}

// Acción por fila (BIC-7B-002): corre la cascada del backfill para UN
// producto. Solo se ofrece en filas sin imagen propia, así que no hay
// riesgo de pisar nada (BIC-7V-02).
async function resolveImageWithIA(prod) {
  iaResolvingId.value = prod.id
  try {
    const { data } = await apiService.post(`/api/products/${prod.id}/image/auto`, {})
    if (data?.resuelto) {
      await loadProducts({ suppressAlert: true, page: currentPage.value })
    } else if (data?.motivo === 'ya_tiene_imagen') {
      window.alert('El producto ya tiene una imagen propia. Reemplazala desde el formulario de edición.')
    } else {
      window.alert('Ninguna fuente encontró una imagen para este producto. Podés cargarla manualmente desde Editar.')
    }
  } catch (error) {
    const msg = error?.response?.data?.message
    window.alert(msg === 'backfill_en_ejecucion'
      ? 'Hay un backfill masivo en ejecución. Probá de nuevo cuando termine.'
      : (msg || 'No se pudo buscar la imagen del producto.'))
  } finally {
    iaResolvingId.value = null
  }
}

// Aplicar paginación a los productos filtrados
const pagedProducts = computed(() => {
  const filtered = filteredProducts.value

  // Paginación local para búsqueda o filtro "sin imagen"
  if (isLocalPagination.value) {
    const start = (currentPage.value - 1) * itemsPerPage
    const end = start + itemsPerPage
    return filtered.slice(start, end)
  }

  // En otros casos (categoría o sin filtros), los productos ya vienen paginados del backend
  return filtered
})

// Debounce para la búsqueda
let searchTimeout = null
watch(searchTerm, (newValue, oldValue) => {
  // Limpiar timeout anterior
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  
  // Si cambia el término de búsqueda, resetear página
  if (newValue !== oldValue) {
    currentPage.value = 1
  }
  
  // Esperar 500ms antes de ejecutar la búsqueda
  searchTimeout = setTimeout(() => {
    loadProducts({ suppressAlert: true, page: 1 })
  }, 500)
})

// Cuando cambia la categoría, recargar productos
watch(selectedCategory, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    currentPage.value = 1
    loadProducts({ suppressAlert: true, page: 1 })
  }
})

// Cuando cambia el filtro "sin imagen", recargar (cambia el modo de carga)
watch(onlyWithoutImage, () => {
  currentPage.value = 1
  loadProducts({ suppressAlert: true, page: 1 })
})

 // Cargar datos al montar el componente
 onMounted(() => {
   loadProducts()
   loadCategories()
 })
</script>

<style scoped>
/* Posición del desplegable del botón partido.
   Bootstrap deja `top`/`left` a cargo de Popper (`.dropdown-menu[data-bs-popper]`)
   y acá el desplegable se abre sin Popper, así que se ancla a mano al
   `.btn-group`, que ya es `position: relative`. */
.new-product-split .dropdown-menu {
  top: 100%;
  right: 0;
  left: auto;
  margin-top: 0.25rem;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Estilos para el dropdown de categorías */
.category-dropdown {
  background: white;
  border: 1px solid #dee2e6;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
}

.dropdown-item {
  transition: background-color 0.15s ease-in-out;
  border-bottom: 1px solid #f8f9fa;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.hover-bg-light:hover {
  background-color: #f8f9fa;
}

.cursor-pointer {
  cursor: pointer;
}

.product-thumb {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fff;
  flex-shrink: 0;
}

.product-thumb--empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  background: #f8fafc;
}

.category-dropdown::-webkit-scrollbar {
  width: 8px;
}

.category-dropdown::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.category-dropdown::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.category-dropdown::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.products-page {
  max-width: 1400px;
}

.page-title-mobile {
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.mobile-filter-card {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 0.82rem;
}

.mobile-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.mobile-results-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1rem 0 1.15rem;
}

.mobile-results-pill {
  display: inline-flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 112px;
  padding: 0.8rem 0.95rem;
  border-radius: 1rem;
  background: #111827;
  color: #fff;
}

.mobile-results-pill.secondary {
  background: #f3f4f6;
  color: #111827;
}

.mobile-results-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.mobile-results-value.small {
  font-size: 0.9rem;
}

.mobile-results-copy {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.8;
}

.mobile-product-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.mobile-product-card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.mobile-product-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
  margin-bottom: 0.85rem;
}

.mobile-product-code {
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

.mobile-product-name {
  margin: 0;
  font-size: 1rem;
  line-height: 1.35;
  color: #0f172a;
}

.mobile-stock-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: #111827;
  color: #fff;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
}

.mobile-stock-badge.low {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fdba74;
}

.mobile-product-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.mobile-meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.85rem;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.mobile-meta-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.mobile-product-note {
  margin: 0.85rem 0 0;
  color: #6b7280;
  font-size: 0.88rem;
}

.mobile-product-actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.95rem;
}

.mobile-product-actions .btn {
  flex: 1 1 0;
}

.mobile-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 1rem;
  background: #f8fafc;
  color: #64748b;
}

@media (max-width: 991.98px) {
  .page-header-mobile {
    align-items: stretch !important;
    flex-direction: column;
    gap: 0.85rem;
  }

  .btn-mobile-primary,
  .mobile-filter-main,
  .mobile-search-input,
  .mobile-category-picker,
  .mobile-clear-btn {
    width: 100%;
  }

  /* En mobile el cuerpo del botón se estira y la flecha queda como un toque
     separado, con área táctil suficiente para no competir con el alta manual. */
  .new-product-split > .btn:first-child {
    flex: 1 1 auto;
  }

  .new-product-split__toggle {
    flex: 0 0 auto;
    min-width: 44px;
    min-height: 44px;
  }

  .mobile-filter-wrap,
  .mobile-filter-main {
    flex-direction: column;
    align-items: stretch !important;
  }
}

@media (max-width: 575.98px) {
  .products-page {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .mobile-product-top,
  .mobile-product-actions {
    flex-direction: column;
  }

  .mobile-stock-badge {
    align-self: flex-start;
  }

  .mobile-product-meta {
    grid-template-columns: 1fr;
  }
}
</style>
